import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
    Shield,
    FileText,
    CheckCircle,
    DollarSign,
    TrendingUp,
    Users,
    Clock,
    AlertCircle,
    Plus,
    ArrowRight,
    BarChart3,
    PieChart,
    Activity,
    Calendar,
    Percent,
    Award,
    Eye,
    Settings,
    ClipboardList
} from 'lucide-react';
import api from '../../services/api';

export default function UnderwriterDashboard() {
    const { user } = useSelector((state) => state.auth);

    // Fetch dashboard stats
    const { data: stats, isLoading: statsLoading } = useQuery('underwriter-stats', () =>
        api.get('/dashboard/stats').then((res) => res.data)
    );

    // Fetch pending quotes specifically
    const { data: pendingQuotesData, isLoading: quotesLoading } = useQuery(
        'pending-quotes',
        () => api.get('/quotes?status=submitted').then((res) => res.data),
        {
            staleTime: 30000, // 30 seconds
            refetchInterval: 60000, // Refetch every minute
        }
    );

    // Fetch recent policies
    const { data: recentPolicies, isLoading: policiesLoading } = useQuery(
        'recent-policies',
        () => api.get('/policies?limit=5').then((res) => res.data),
        {
            staleTime: 30000,
            refetchInterval: 60000,
        }
    );

    // Fetch all policies for count
    const { data: allPolicies, isLoading: policiesCountLoading } = useQuery(
        'all-policies-count',
        () => api.get('/policies').then((res) => res.data),
        {
            staleTime: 30000,
        }
    );

    if (statsLoading) return <SkeletonLoader />;

    // Calculate metrics with proper fallbacks
    const totalQuotes = stats?.totalQuotes || 0;
    const totalPolicies = stats?.policies || allPolicies?.length || 0;
    const totalPremium = stats?.totalCommissions || 0;
    const activePolicies = stats?.activePolicies || allPolicies?.filter(p => p.payment_status === 'paid').length || 0;

    // Get pending quotes count and data
    const pendingQuotesList = pendingQuotesData?.data || pendingQuotesData || [];
    const pendingQuotes = Array.isArray(pendingQuotesList) ? pendingQuotesList.length : 0;

    // Get recent quotes (pending)
    const recentQuotes = Array.isArray(pendingQuotesList) ? pendingQuotesList.slice(0, 5) : [];

    // Get recent policies
    const recentPoliciesList = recentPolicies?.data || recentPolicies || [];
    const policiesList = Array.isArray(recentPoliciesList) ? recentPoliciesList : [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Hero Banner */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Shield className="w-5 h-5 text-green-300" />
                                <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Underwriter Dashboard</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-extrabold">
                                Welcome back, {user?.full_name || 'Underwriter'} 👋
                            </h1>
                            <p className="text-green-100/80 mt-2 max-w-2xl">
                                Review quotes, manage policies, and oversee underwriting operations from one centralized dashboard.
                            </p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <Link
                                to="/quotes?status=submitted"
                                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white hover:text-[#3a663f] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <ClipboardList className="w-5 h-5" />
                                Review Quotes {pendingQuotes > 0 && `(${pendingQuotes})`}
                            </Link>
                            <Link
                                to="/policies"
                                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white font-medium py-3 px-6 rounded-xl hover:bg-white/20 transition-all duration-300"
                            >
                                <Eye className="w-5 h-5" />
                                View Policies
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        icon={Clock}
                        label="Pending Quotes"
                        value={pendingQuotes}
                        subtitle="Awaiting review"
                        gradient="from-[#2d5232] to-[#3a663f]"
                    />
                    <StatCard
                        icon={CheckCircle}
                        label="Active Policies"
                        value={activePolicies}
                        subtitle="In force"
                        gradient="from-[#3a663f] to-[#4d8c54]"
                    />
                    <StatCard
                        icon={FileText}
                        label="Total Quotes"
                        value={totalQuotes}
                        subtitle="All time"
                        gradient="from-[#4d8c54] to-[#5fa06a]"
                    />
                    <StatCard
                        icon={DollarSign}
                        label="Premium Volume"
                        value={`$${totalPremium.toLocaleString()}`}
                        subtitle="Total underwriting"
                        gradient="from-[#5fa06a] to-[#7ab885]"
                    />
                </div>

                {/* Quick Actions Row */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <QuickAction
                        icon={ClipboardList}
                        label="Review Quotes"
                        description={`${pendingQuotes} pending review`}
                        to="/quotes?status=submitted"
                        color="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                        iconColor="text-blue-600 dark:text-blue-400"
                    />
                    <QuickAction
                        icon={CheckCircle}
                        label="View Policies"
                        description={`${activePolicies} active`}
                        to="/policies"
                        color="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                        iconColor="text-green-600 dark:text-green-400"
                    />
                    <QuickAction
                        icon={BarChart3}
                        label="Dashboard Stats"
                        description="View analytics"
                        to="/dashboard"
                        color="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800"
                        iconColor="text-purple-600 dark:text-purple-400"
                    />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pending Quotes */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h2 className="font-bold text-gray-800 dark:text-white">Pending Quotes</h2>
                                <span className="text-sm text-gray-500 dark:text-slate-400">({pendingQuotes})</span>
                            </div>
                            <Link
                                to="/quotes?status=submitted"
                                className="text-sm text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium flex items-center gap-1 transition-colors"
                            >
                                View All
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="p-6">
                            {quotesLoading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="h-16 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                                    ))}
                                </div>
                            ) : recentQuotes.length > 0 ? (
                                <div className="space-y-3">
                                    {recentQuotes.map((quote) => (
                                        <Link
                                            key={quote.id}
                                            to={`/quotes/${quote.id}`}
                                            className="block p-3 bg-gray-50 dark:bg-slate-700/30 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-gray-800 dark:text-white group-hover:text-[#3a663f] dark:group-hover:text-green-400 transition-colors">
                                                        {quote.quote_number}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
                                                        <span>{quote.vessel_name || 'N/A'}</span>
                                                        <span>•</span>
                                                        <span>${Number(quote.total_premium || quote.total_amount || 0).toLocaleString()}</span>
                                                        <span>•</span>
                                                        <span>{quote.insured_name || quote.client_name || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <span className="px-2.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium rounded-full flex-shrink-0">
                                                    Pending
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
                                        <CheckCircle className="w-8 h-8 text-gray-300 dark:text-slate-500" />
                                    </div>
                                    <p className="text-gray-500 dark:text-slate-400">No pending quotes</p>
                                    <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">All quotes have been reviewed! 🎉</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Policies */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h2 className="font-bold text-gray-800 dark:text-white">Recent Policies</h2>
                                <span className="text-sm text-gray-500 dark:text-slate-400">({activePolicies})</span>
                            </div>
                            <Link
                                to="/policies"
                                className="text-sm text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium flex items-center gap-1 transition-colors"
                            >
                                View All
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="p-6">
                            {policiesLoading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="h-16 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                                    ))}
                                </div>
                            ) : policiesList.length > 0 ? (
                                <div className="space-y-3">
                                    {policiesList.slice(0, 5).map((policy) => (
                                        <Link
                                            key={policy.id}
                                            to={`/policies/${policy.id}`}
                                            className="block p-3 bg-gray-50 dark:bg-slate-700/30 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-gray-800 dark:text-white group-hover:text-[#3a663f] dark:group-hover:text-green-400 transition-colors">
                                                        {policy.policy_number}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
                                                        <span>{policy.vessel_name || 'N/A'}</span>
                                                        <span>•</span>
                                                        <span>${Number(policy.sum_insured).toLocaleString()}</span>
                                                        <span>•</span>
                                                        <span>{policy.insured_name || policy.client_name || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${policy.payment_status === 'paid' || policy.payment_status === 'active'
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                                                    }`}>
                                                    {policy.payment_status === 'paid' || policy.payment_status === 'active' ? 'Active' : 'Pending'}
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
                                        <FileText className="w-8 h-8 text-gray-300 dark:text-slate-500" />
                                    </div>
                                    <p className="text-gray-500 dark:text-slate-400">No policies yet</p>
                                    <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">Policies will appear here once issued</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Performance Summary */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                            <h2 className="font-bold text-gray-800 dark:text-white">Performance Summary</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <PerformanceItem
                                label="Conversion Rate"
                                value={totalQuotes > 0 ? Math.round((activePolicies / totalQuotes) * 100) : 0}
                                suffix="%"
                                color="text-[#3a663f] dark:text-green-400"
                            />
                            <PerformanceItem
                                label="Avg Premium per Policy"
                                value={activePolicies > 0 ? Math.round(totalPremium / activePolicies) : 0}
                                prefix="$"
                                color="text-blue-600 dark:text-blue-400"
                            />
                            <PerformanceItem
                                label="Total Policies"
                                value={totalPolicies}
                                color="text-purple-600 dark:text-purple-400"
                            />
                            <PerformanceItem
                                label="Total Premium Volume"
                                value={totalPremium}
                                prefix="$"
                                color="text-amber-600 dark:text-amber-400"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function StatCard({ icon: Icon, label, value, subtitle, gradient }) {
    return (
        <div className={`bg-gradient-to-br ${gradient} text-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm opacity-80">{label}</p>
                    <p className="text-2xl font-extrabold mt-1">{value}</p>
                    {subtitle && <p className="text-xs opacity-60 mt-1">{subtitle}</p>}
                </div>
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </div>
    );
}

function QuickAction({ icon: Icon, label, description, to, color, iconColor }) {
    return (
        <Link
            to={to}
            className={`flex items-center gap-3 p-4 rounded-xl border ${color} hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group`}
        >
            <div className={`p-2 rounded-lg bg-white dark:bg-slate-800 shadow-sm ${iconColor}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1">
                <p className="font-medium text-gray-800 dark:text-white">{label}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">{description}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
        </Link>
    );
}

function PerformanceItem({ label, value, prefix = '', suffix = '', color }) {
    return (
        <div className="text-center p-4 bg-gray-50 dark:bg-slate-700/30 rounded-xl">
            <p className="text-sm text-gray-500 dark:text-slate-400">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>
                {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </p>
        </div>
    );
}

function SkeletonLoader() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="animate-pulse space-y-6">
                    <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
                    <div className="grid grid-cols-4 gap-5">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-28 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                        ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-20 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div className="h-96 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                        <div className="h-96 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                    </div>
                    <div className="h-32 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                </div>
            </div>
        </div>
    );
}